import React, { useState, useEffect } from "react";
import DynamicForm, { FieldConfig } from "./DynamicForm";
import { FieldType } from "../interfaces/FieldType";

interface MultiItemFormProps {
  fields: FieldConfig[];
  items: any[];
  onChange: (items: any[]) => void;
  title?: string;

  /**
   * This is the callback if you want a
   * "Save All" button inside the sub-form.
   * We'll pass `localItems` to it on click.
   */
  onSaveAll?: (localItems: any[]) => void;
}

const MultiItemForm: React.FC<MultiItemFormProps> = ({
  fields,
  items,
  onChange,
  onSaveAll,
  title,
}) => {
  const [localItems, setLocalItems] = useState<any[]>([]);

  useEffect(() => {
    // Sync local state from parent
    setLocalItems(items);
  }, [items]);

  const handleItemChange = (index: number, updatedData: any) => {
    const newItems = [...localItems];
    newItems[index] = updatedData;
    setLocalItems(newItems);
    onChange(newItems);
  };

  const handleAddItem = () => {
    const emptyItem: any = {};
    fields.forEach((f) => {
      emptyItem[f.key] = f.type === FieldType.CHECKBOX ? false : "";
    });

    const newItems = [...localItems, emptyItem];
    setLocalItems(newItems);
    onChange(newItems); // ✅ pushes to parent
  };

  const handleRemoveItem = (index: number) => {
    const newItems = localItems.filter((_, i) => i !== index);
    setLocalItems(newItems);
    onChange(newItems);
  };

  const handleSaveAllClick = () => {
    // We ensure parent's onChange is up to date, then call onSaveAll
    onChange(localItems);

    if (onSaveAll) {
      onSaveAll(localItems);
    }
  };

  return (
    <div className="multi-item-form">
      {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}

      {localItems.map((itemData, index) => (
        <div key={index} className="border p-3 mb-4 rounded">
          <div className="flex justify-between items-center mb-2">
            <span>
              {title} #{index + 1}
            </span>
            <button
              type="button"
              className="text-red-500 underline"
              onClick={() => handleRemoveItem(index)}
            >
              Remove
            </button>
          </div>

          <DynamicForm
            fields={fields}
            defaultValues={itemData}
            showSubmit={false}
            showCancel={false}
            onChange={(data) => handleItemChange(index, data)}
          />
        </div>
      ))}

      <div className="flex items-end justify-between w-full">
        <button
          type="button"
          className="bg-primary-100 border-2 border-primary-400 text-white px-4 py-2 rounded-2xl hover:bg-primary-50"
          onClick={handleAddItem}
        >
          + Add
        </button>

        {onSaveAll && (
          <button
            type="button"
            className="border-tertiary-500 border-2 text-white px-4 py-2 rounded-2xl hover:bg-tertiary-200"
            onClick={handleSaveAllClick}
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
};

export default MultiItemForm;
